import { Alert, Stack, Typography } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { isNil } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { ICliJob } from 'cli/types';
import { CheckboxInput, Confirmation } from 'core/components';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { INotification } from 'core/types';
import { renderHtmlMessage } from 'core/utils';
import { useQuotePricingWindowMessage } from 'quotes/hooks';
import {
  mapApplySelectedSpaJobProgress,
  mapApplySelectedSpaJobResult,
  mapApplySelectedSpaToLinesRequest,
} from 'quotes/mappers';
import { quotePartService } from 'quotes/services';
import {
  IApplySelectedSpaJobProgress,
  IApplySelectedSpaOptions,
  IApplySelectedSpaToLinesRequest,
  IApplySelectedSpaToLinesSource,
  ICustomerQuotePart,
} from 'quotes/types';
import { ISelectedSpaPick } from 'quotes/utils';
import { Color, Level, Size, Variant } from 'theme/enums';

import {
  getApplySelectedSpaJobProgress,
  IFinishedApplySelectedSpaJob,
  isApplySelectedSpaJobPending,
  parkApplySelectedSpaJobResult,
  setApplySelectedSpaJobPending,
  setApplySelectedSpaJobProgress,
  takeApplySelectedSpaJobResult,
  useApplySelectedSpaJobProgress,
  useHasParkedApplySelectedSpaJobResult,
  useIsApplySelectedSpaJobPending,
} from './applySelectedSpaJobs';
import { ApplySelectedSpaResultDialog } from '../ApplySelectedSpaResultDialog';
import {
  countApplySelectedSpaLines,
  DEFAULT_APPLY_SELECTED_SPA_OPTIONS,
  getAppliedSelectedSpaCode,
  getApplySelectedSpaStopDescription,
  hasApplySelectedSpaCandidates,
  hasApplySelectedSpaJobProgress,
  IApplySelectedSpaLineCounts,
  mayApplySelectedSpaJobHaveSavedLines,
  shouldReloadAfterApplySelectedSpaJob,
} from '../applyToLines';

interface IProps {
  quoteId: number;
  onApplied?: () => void;
}

interface IPendingApply {
  agreementCode: string;
  lineCounts: IApplySelectedSpaLineCounts;
  source: IApplySelectedSpaToLinesSource;
}

const isJobLoadingToast = (shown: INotification | null, jobMessage: string): boolean =>
  ApiStatus.LOADING === shown?.type &&
  NotificationPosition.TOP === shown?.position &&
  jobMessage === shown?.message;

/**
 * Render the returned node as a sibling of the drawer: the Steps drawer unmounts its content ~1s
 * after a successful submit.
 */
export const useApplySelectedSpaToLines = ({ quoteId, onApplied }: IProps) => {
  const [pendingApply, setPendingApply] = useState<IPendingApply | null>(null);
  const [applyOptions, setApplyOptions] = useState<IApplySelectedSpaOptions>(
    DEFAULT_APPLY_SELECTED_SPA_OPTIONS
  );
  const [finishedApply, setFinishedApply] = useState<IFinishedApplySelectedSpaJob | null>(null);

  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const { gridApi, rows, updateRows } = useAgGridClientTableContext();
  // Read live: the job's callbacks outlive the render, and the host that started it.
  const {
    clearNotification,
    getNotification,
    showErrorNotification,
    showLoadingNotification,
    showWarningNotification,
  } = useNotificationContext();
  const { handleCliJob } = useCliJobHandler();
  const isJobPending = useIsApplySelectedSpaJobPending(quoteId);
  const jobProgress = useApplySelectedSpaJobProgress(quoteId);
  const hasParkedResult = useHasParkedApplySelectedSpaJobResult(quoteId);
  // The BE refuses the job while the engine is paused for the quote; the prompt says so instead.
  const pricingWindowMessage = useQuotePricingWindowMessage(quoteId);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // The results of a job that ended while no host of this quote was mounted: shown once, here.
  useEffect(() => {
    if (!hasParkedResult) {
      return;
    }

    const parked = takeApplySelectedSpaJobResult(quoteId);

    if (parked) {
      setFinishedApply(parked);
    }
  }, [hasParkedResult, quoteId]);

  // The dialog is the only report of the skipped lines, so a host that is gone leaves them.
  const showResult = (jobQuoteId: number, finished: IFinishedApplySelectedSpaJob) => {
    if (isMountedRef.current) {
      setFinishedApply(finished);

      return;
    }

    parkApplySelectedSpaJobResult(jobQuoteId, finished);
  };

  const reloadQuoteParts = () => queryClient.invalidateQueries('customerQuoteParts');

  // Once the starting host is gone, only a reload reaches a grid loaded mid-job.
  const refreshAppliedRows = (quoteParts: ICustomerQuotePart[], shouldReload: boolean) => {
    const isHostGone = !isMountedRef.current;

    if (quoteParts.length && !isHostGone) {
      updateRows({ update: quoteParts }, { refreshCells: true, redrawRows: true });
    }

    if (shouldReload || (quoteParts.length && isHostGone)) {
      reloadQuoteParts();
    }
  };

  const promptApplyToOtherLines = ({
    savedQuotePart,
    previousSelectedSpa,
  }: {
    savedQuotePart?: ICustomerQuotePart | null;
    previousSelectedSpa?: ISelectedSpaPick | null;
  }) => {
    const agreementCode = getAppliedSelectedSpaCode(savedQuotePart, previousSelectedSpa);

    if (!agreementCode || !quoteId || !savedQuotePart?.id) {
      return;
    }

    const lineCounts = countApplySelectedSpaLines(rows as ICustomerQuotePart[], savedQuotePart);

    if (!hasApplySelectedSpaCandidates(lineCounts, DEFAULT_APPLY_SELECTED_SPA_OPTIONS)) {
      return;
    }

    setApplyOptions(DEFAULT_APPLY_SELECTED_SPA_OPTIONS);
    setPendingApply({
      agreementCode,
      lineCounts,
      source: {
        quoteId,
        sourceQuotePartId: savedQuotePart.id,
        // The raw stored code: the BE refuses a code that differs from what the line holds.
        spaCostSource: savedQuotePart.costSourceSpaCostSource || agreementCode,
        spaCostSourceId: savedQuotePart.costSourceSpaCostSourceId,
      },
    });
  };

  const onJobFinished = (jobQuoteId: number, agreementCode: string, cliJob: ICliJob) => {
    const result = mapApplySelectedSpaJobResult(cliJob.result);

    refreshAppliedRows(result.quoteParts, shouldReloadAfterApplySelectedSpaJob(result));

    if (result.quoteParts.length || result.applied.length) {
      onApplied?.();
    }

    showResult(jobQuoteId, { agreementCode, isStopped: false, result });
  };

  const onJobStopped = (
    jobQuoteId: number,
    agreementCode: string,
    cliJob: ICliJob,
    lastProgress: IApplySelectedSpaJobProgress | null
  ) => {
    const result = mapApplySelectedSpaJobResult(cliJob.result);

    // A job that died keeps its last progress as its result.
    refreshAppliedRows(
      result.quoteParts,
      shouldReloadAfterApplySelectedSpaJob(result, {
        isStopped: true,
        lastProgress: mapApplySelectedSpaJobProgress(cliJob.result) ?? lastProgress,
      })
    );

    // Each line commits on its own, so lines may have changed even when the job reports none.
    onApplied?.();

    showErrorNotification(
      {
        title: t('errorTitleSelectedSpaApply'),
        message: t('messageSelectedSpaApplyInterrupted', {
          description: getApplySelectedSpaStopDescription(
            cliJob,
            t('messageSelectedSpaApplyJobExpired')
          ),
        }),
        disableAutoClose: true,
      },
      NotificationPosition.TOP
    );

    if (hasApplySelectedSpaJobProgress(result)) {
      showResult(jobQuoteId, { agreementCode, isStopped: true, result });
    }
  };

  const onConfirm = () => {
    if (
      !pendingApply ||
      pricingWindowMessage ||
      isApplySelectedSpaJobPending(pendingApply.source.quoteId)
    ) {
      return;
    }

    const { agreementCode, source } = pendingApply;
    const request = mapApplySelectedSpaToLinesRequest(source, applyOptions);
    const jobQuoteId = request.quoteId;

    const inProgressMessage = t('messageSelectedSpaApplyInProgress', { agreementCode });
    let lastProgressMessage = inProgressMessage;

    setPendingApply(null);
    setApplySelectedSpaJobPending(jobQuoteId, true);

    const onJobProgress = (cliJob: ICliJob) => {
      // None before the lines are counted, nor once a `running` job holds its final result.
      const progress = mapApplySelectedSpaJobProgress(cliJob.result);

      if (!progress) {
        return;
      }

      setApplySelectedSpaJobProgress(jobQuoteId, progress);

      const message = t('messageSelectedSpaApplyProgress', {
        agreementCode,
        applied: progress.applied,
        count: progress.total,
        processed: progress.processed,
        skipped: progress.skipped,
      });
      const shown = getNotification();
      // One notification slot: replace only this job's own toast or an empty slot.
      const isSlotOurs = !shown || isJobLoadingToast(shown, lastProgressMessage);

      if (message === lastProgressMessage || !isSlotOurs) {
        return;
      }

      lastProgressMessage = message;
      showLoadingNotification({ message }, NotificationPosition.TOP);
    };

    // handleCliJob reports a failed start itself but calls neither job callback.
    const startJob = async (payload: IApplySelectedSpaToLinesRequest) => {
      try {
        return await quotePartService.applySelectedSpaToLines(payload);
      } catch (error) {
        setApplySelectedSpaJobPending(jobQuoteId, false);

        throw error;
      }
    };

    void handleCliJob(startJob, request, {
      cliJobInProgressMessage: inProgressMessage,
      cliJobErrorMessage: t('messageSelectedSpaApplyStartFailed', { agreementCode }),
      preferBackendError: true,
      onCliJobFinished: (cliJob: ICliJob) => {
        setApplySelectedSpaJobPending(jobQuoteId, false);

        // The dialog is the report; cleared before onApplied, whose host toast must survive.
        if (isJobLoadingToast(getNotification(), lastProgressMessage)) {
          clearNotification();
        }

        onJobFinished(jobQuoteId, agreementCode, cliJob);
      },
      onCliJobStopped: (cliJob: ICliJob) => {
        // Read before the pending flag goes: clearing it drops the progress too.
        const lastProgress = getApplySelectedSpaJobProgress(jobQuoteId);

        setApplySelectedSpaJobPending(jobQuoteId, false);
        onJobStopped(jobQuoteId, agreementCode, cliJob, lastProgress);
      },
      onCliJobProgress: onJobProgress,
      onCliJobPollFailed: () => {
        const lastProgress = getApplySelectedSpaJobProgress(jobQuoteId);

        setApplySelectedSpaJobPending(jobQuoteId, false);
        showWarningNotification(
          { message: t('messageSelectedSpaApplyLostTrack') },
          NotificationPosition.TOP
        );

        if (mayApplySelectedSpaJobHaveSavedLines(lastProgress)) {
          reloadQuoteParts();
          onApplied?.();
        }
      },
    });
  };

  const jobPendingMessage = jobProgress
    ? t('messageSelectedSpaApplyJobPendingProgress', {
        count: jobProgress.total,
        processed: jobProgress.processed,
      })
    : t('messageSelectedSpaApplyJobPending');

  const toggleApplyOption = (option: keyof IApplySelectedSpaOptions) =>
    setApplyOptions((previous) => ({ ...previous, [option]: !previous[option] }));

  const confirmationMessage = (
    <Stack spacing={2}>
      {renderHtmlMessage(
        t('messageConfirmApplySelectedSpaToLines', { agreementCode: pendingApply?.agreementCode })
      )}
      <Stack spacing={1}>
        <CheckboxInput
          label={t('labelSelectedSpaApplyReplaceManualCosts', {
            count: pendingApply?.lineCounts.manualCost ?? 0,
          })}
          value={applyOptions.replaceManualCosts}
          onChange={() => toggleApplyOption('replaceManualCosts')}
          sx={{ fontWeight: 600 }}
        />
        <CheckboxInput
          label={t('labelSelectedSpaApplyReplaceSelectedSpas', {
            count: pendingApply?.lineCounts.otherSelectedSpa ?? 0,
          })}
          value={applyOptions.replaceSelectedSpas}
          onChange={() => toggleApplyOption('replaceSelectedSpas')}
          sx={{ fontWeight: 600 }}
        />
        <Typography level={Level.BODY_SM}>{t('messageSelectedSpaApplyKeepsCosts')}</Typography>
      </Stack>
      <Alert
        startDecorator={<Warning style={{ width: 24, height: 24 }} />}
        variant={Variant.SOFT}
        color={Color.WARNING}
        size={Size.LG}
        sx={{
          alignItems: 'flex-start',
          width: '100%',

          '& .MuiAlert-startDecorator': {
            marginTop: '2px',
          },
        }}
      >
        <Typography level={Level.BODY_SM}>
          {pricingWindowMessage || t('messageSelectedSpaApplyRunsInBackground')}
        </Typography>
      </Alert>
      {isJobPending && renderHtmlMessage(jobPendingMessage)}
    </Stack>
  );

  const scrollToLine = (quotePartId: number) => {
    const rowNode = gridApi?.getRowNode(String(quotePartId));

    // Filtered out or inside a collapsed group: not displayed, nothing to scroll to.
    if (!rowNode || isNil(rowNode.rowIndex)) {
      return;
    }

    gridApi.ensureIndexVisible(rowNode.rowIndex, 'middle');

    setTimeout(() => {
      gridApi.flashCells({ rowNodes: [rowNode], flashDuration: 2000, fadeDuration: 1000 });
    }, 500);
  };

  // The scroll waits for the dialog's unmount, whose focus restore could scroll the grid back.
  const onGoToLine = (quotePartId: number) => {
    setFinishedApply(null);

    setTimeout(() => scrollToLine(quotePartId));
  };

  const applyToLinesConfirmation = (
    <>
      <Confirmation
        isOpen={!!pendingApply}
        title={t('titleApplySelectedSpaToLines')}
        message={confirmationMessage}
        confirmBtnDisabled={isJobPending || !!pricingWindowMessage}
        onConfirm={onConfirm}
        onCancel={() => setPendingApply(null)}
      />

      <ApplySelectedSpaResultDialog
        result={finishedApply?.result ?? null}
        gridRows={rows as ICustomerQuotePart[]}
        agreementCode={finishedApply?.agreementCode}
        onLineClick={onGoToLine}
        onClose={() => setFinishedApply(null)}
        isStopped={finishedApply?.isStopped}
      />
    </>
  );

  return { promptApplyToOtherLines, applyToLinesConfirmation };
};
